import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  RefreshCw, Loader2, CheckCircle2, XCircle, Clock, Shield,
  AlertTriangle, Package, ChevronDown, ChevronUp, Zap,
  Ban, RotateCcw, Search, TrendingUp, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


// ── Score Badge ──
function ScoreBadge({ score }) {
  const color = score >= 900 ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
    : score >= 800 ? 'text-blue-400 border-blue-500/30 bg-blue-500/10'
    : score >= 700 ? 'text-amber-400 border-amber-500/30 bg-amber-500/10'
    : score >= 300 ? 'text-orange-400 border-orange-500/30 bg-orange-500/10'
    : 'text-red-400 border-red-500/30 bg-red-500/10';
  return <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border ${color}`}>{Math.round(score)}</span>;
}

// ── Verification Check Row ──
function CheckRow({ check }) {
  const icon = check.status === 'passed' ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
    : check.status === 'failed' ? <XCircle className="w-3.5 h-3.5 text-red-400" />
    : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[10px] text-gray-400">{check.check.replace(/_/g, ' ')}</span>
      </div>
      <span className="text-[10px] font-mono text-gray-500">{check.score}pts</span>
    </div>
  );
}

// ── Registration Card ──
function RegistrationCard({ reg, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const v = reg.verification_report;
  const statusColors = {
    verified: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
    approved: 'text-blue-400 border-blue-500/30 bg-blue-500/10',
    rejected: 'text-red-400 border-red-500/30 bg-red-500/10',
    submitted: 'text-amber-400 border-amber-500/30 bg-amber-500/10',
  };

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 hover:border-white/8 transition-colors" data-testid={`reg-card-${reg.registration_id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/5 flex items-center justify-center shrink-0">
            <Package className="w-4 h-4 text-[#F7931A]" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate">{reg.company_name || 'Unnamed'}</p>
            <p className="text-[10px] text-gray-500">{reg.contact_email} | {reg.registration_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className={`text-[9px] ${statusColors[reg.status] || 'text-gray-400'}`}>
            {reg.status}
          </Badge>
          {v && <span className="text-[10px] font-mono text-gray-500">{v.total_score}/{v.max_score}</span>}
          <button onClick={() => setExpanded(!expanded)} className="text-gray-600 hover:text-gray-300">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
          {/* Verification Checks */}
          {v?.checks && (
            <div>
              <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-1">Anti-Sybil Verification</p>
              {v.checks.map((c, i) => <CheckRow key={i} check={c} />)}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                <span className="text-[10px] text-gray-400">Recommendation</span>
                <Badge variant="outline" className={`text-[9px] ${
                  v.recommendation === 'auto_approve' ? 'text-emerald-400 border-emerald-500/30' :
                  v.recommendation === 'reject' ? 'text-red-400 border-red-500/30' :
                  'text-amber-400 border-amber-500/30'
                }`}>{v.recommendation?.replace(/_/g, ' ')}</Badge>
              </div>
            </div>
          )}

          {/* Mint Preview */}
          {reg.mint_preview && (
            <div>
              <p className="text-[9px] text-gray-600 uppercase tracking-wider mb-1">Mint Preview (Era {reg.mint_preview.era})</p>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white/[0.02] rounded-lg p-2 text-center">
                  <p className="text-xs font-mono font-bold text-[#F7931A]">{reg.mint_preview.mint_amount?.toLocaleString()}</p>
                  <p className="text-[8px] text-gray-600">Total Mint</p>
                </div>
                <div className="bg-white/[0.02] rounded-lg p-2 text-center">
                  <p className="text-xs font-mono font-bold text-emerald-400">{reg.mint_preview.distribution?.staked_on_protocol?.toLocaleString()}</p>
                  <p className="text-[8px] text-gray-600">Staked (50%)</p>
                </div>
                <div className="bg-white/[0.02] rounded-lg p-2 text-center">
                  <p className="text-xs font-mono font-bold text-blue-400">{reg.mint_preview.distribution?.guest_wallet?.toLocaleString()}</p>
                  <p className="text-[8px] text-gray-600">Guest Wallet (20%)</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {reg.status === 'verified' && (
            <div className="flex items-center gap-2 pt-2">
              <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white h-7" onClick={() => onApprove(reg.registration_id)} data-testid={`approve-btn-${reg.registration_id}`}>
                <CheckCircle2 className="w-3 h-3 mr-1" />Approve & Mint
              </Button>
              <Button size="sm" variant="outline" className="text-xs border-red-500/30 text-red-400 hover:bg-red-500/10 h-7" onClick={() => onReject(reg.registration_id)} data-testid={`reject-btn-${reg.registration_id}`}>
                <XCircle className="w-3 h-3 mr-1" />Reject
              </Button>
            </div>
          )}

          {reg.status === 'approved' && reg.vendor_id && (
            <p className="text-[10px] text-emerald-400">Approved — Vendor ID: {reg.vendor_id}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Staged Mint Card ──
function StagedMintCard({ staged, onRelease, onClawback }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4" data-testid={`staged-card-${staged.vendor_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-medium text-white">{staged.vendor_id}</p>
          <p className="text-[10px] text-gray-500">Era {staged.era} | {staged.total_mint?.toLocaleString()} HERMES total</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`text-[9px] ${staged.status === 'active' ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400 border-red-500/30'}`}>
            {staged.status}
          </Badge>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-white/[0.03] rounded-full mb-3 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#F7931A] to-[#B08D57] rounded-full transition-all" style={{ width: `${(staged.released_amount / staged.total_mint) * 100}%` }} />
      </div>
      <div className="flex justify-between text-[9px] text-gray-500 mb-3">
        <span>Released: {staged.released_amount?.toLocaleString()}</span>
        <span>Escrowed: {staged.escrowed_amount?.toLocaleString()}</span>
      </div>

      {/* Phases */}
      <div className="grid grid-cols-4 gap-2">
        {staged.phases?.map(p => (
          <div key={p.phase} className={`rounded-lg p-2 text-center border ${p.status === 'released' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/[0.01] border-white/5'}`}>
            <div className="flex items-center justify-center mb-1">
              {p.status === 'released' ? <Unlock className="w-3 h-3 text-emerald-400" /> : <Lock className="w-3 h-3 text-gray-600" />}
            </div>
            <p className="text-[10px] font-mono font-bold text-white">{(p.pct * 100)}%</p>
            <p className="text-[8px] text-gray-600">Day {p.phase === 1 ? 0 : (p.phase - 1) * 30}</p>
            {p.status === 'pending' && staged.status === 'active' && (
              <button onClick={() => onRelease(staged.vendor_id, p.phase)} className="mt-1 text-[8px] text-[#F7931A] hover:text-[#F7931A]/80" data-testid={`release-phase-${p.phase}`}>
                Release
              </button>
            )}
          </div>
        ))}
      </div>

      {staged.status === 'active' && staged.escrowed_amount > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <Button size="sm" variant="outline" className="text-[10px] h-6 border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => onClawback(staged.vendor_id)} data-testid={`clawback-btn-${staged.vendor_id}`}>
            <Ban className="w-3 h-3 mr-1" />Clawback Escrowed ({staged.escrowed_amount?.toLocaleString()})
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Vendor Quality Row ──
function VendorQualityRow({ vendor, onCheckDelist, onReinstate }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`quality-row-${vendor.vendor_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center">
          <Package className="w-4 h-4 text-[#F7931A]" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-200 truncate">{vendor.company_name || vendor.vendor_id}</p>
          <p className="text-[10px] text-gray-600">{(vendor.categories || []).join(', ')}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <ScoreBadge score={vendor.quality_score || 0} />
        <Badge variant="outline" className={`text-[9px] ${vendor.status === 'active' ? 'text-emerald-400 border-emerald-500/30' : vendor.status === 'delisted' ? 'text-red-400 border-red-500/30' : 'text-gray-400'}`}>
          {vendor.status}
        </Badge>
        {vendor.status === 'active' && (
          <Button size="sm" variant="ghost" className="text-[9px] h-6 text-gray-500 hover:text-white" onClick={() => onCheckDelist(vendor.vendor_id)}>
            <Search className="w-3 h-3 mr-1" />Check
          </Button>
        )}
        {vendor.status === 'delisted' && (
          <Button size="sm" variant="ghost" className="text-[9px] h-6 text-emerald-400 hover:text-emerald-300" onClick={() => onReinstate(vendor.vendor_id)}>
            <RotateCcw className="w-3 h-3 mr-1" />Reinstate
          </Button>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════

export default function VendorManagementTab() {
  const [tab, setTab] = useState('queue');
  const [registrations, setRegistrations] = useState([]);
  const [stagedMints, setStagedMints] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [regsRes, vendorsRes] = await Promise.allSettled([
        fetch(`${API}/api/blockchain/v2/vendor-registration/`).then(r => r.json()),
        fetch(`${API}/api/vendor/list`).then(r => r.json()),
      ]);

      if (regsRes.status === 'fulfilled') setRegistrations(regsRes.value?.registrations || []);
      if (vendorsRes.status === 'fulfilled') {
        const vlist = vendorsRes.value?.vendors || vendorsRes.value || [];
        setVendors(Array.isArray(vlist) ? vlist : []);

        // Load staged mints for approved vendors
        const approvedVendorIds = (regsRes.value?.registrations || [])
          .filter(r => r.vendor_id)
          .map(r => r.vendor_id);

        const staged = [];
        for (const vid of approvedVendorIds) {
          try {
            const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/mint-phases/${vid}`);
            const data = await res.json();
            if (!data.error) staged.push(data);
          } catch {}
        }
        setStagedMints(staged);
      }
    } catch { toast.error('Failed to load vendor data'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async (regId) => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/${regId}/approve?approver_id=admin`, { method: 'POST' });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success(`Vendor approved! ID: ${data.vendor_id}`);
      loadData();
    } catch { toast.error('Failed to approve'); }
  };

  const handleReject = async (regId) => {
    try {
      await fetch(`${API}/api/blockchain/v2/vendor-registration/${regId}/reject?reason=Admin+rejected&rejector_id=admin`, { method: 'POST' });
      toast.success('Registration rejected');
      loadData();
    } catch { toast.error('Failed to reject'); }
  };

  const handleReleasePhase = async (vendorId, phase) => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/mint-phases/${vendorId}/release/${phase}`, { method: 'POST' });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success(`Phase ${phase} released for ${vendorId}`);
      loadData();
    } catch { toast.error('Failed to release phase'); }
  };

  const handleClawback = async (vendorId) => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/mint-phases/${vendorId}/clawback?reason=Admin+clawback`, { method: 'POST' });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success(`Clawed back ${data.clawed_back?.toLocaleString()} HERMES from ${vendorId}`);
      loadData();
    } catch { toast.error('Failed to clawback'); }
  };

  const handleCheckDelist = async (vendorId) => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/delist/check/${vendorId}`, { method: 'POST' });
      const data = await res.json();
      if (data.delisted) {
        toast.error(`Vendor ${vendorId} delisted — score ${data.score}`);
      } else {
        toast.success(`Vendor safe — score ${data.score} (threshold: 300)`);
      }
      loadData();
    } catch { toast.error('Check failed'); }
  };

  const handleBulkCheck = async () => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/delist/bulk-check`, { method: 'POST' });
      const data = await res.json();
      toast.success(`Checked ${data.vendors_checked} vendors — ${data.vendors_delisted} delisted`);
      loadData();
    } catch { toast.error('Bulk check failed'); }
  };

  const handleReinstate = async (vendorId) => {
    try {
      const res = await fetch(`${API}/api/blockchain/v2/vendor-registration/delist/reinstate/${vendorId}`, { method: 'POST' });
      const data = await res.json();
      if (data.reinstated) toast.success(`Vendor ${vendorId} reinstated`);
      else toast.error(data.message);
      loadData();
    } catch { toast.error('Reinstate failed'); }
  };

  const pendingCount = registrations.filter(r => r.status === 'verified').length;
  const activeStaged = stagedMints.filter(s => s.status === 'active').length;
  const delistedCount = vendors.filter(v => v.status === 'delisted').length;

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#F7931A]" /></div>;
  }

  return (
    <div className="px-4 lg:px-6 py-6 space-y-6" data-testid="vendor-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl heading-font font-bold tracking-tight text-white" data-testid="vendor-mgmt-heading">
            Vendor Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">Registration queue, staged minting, quality enforcement</p>
        </div>
        <Button size="sm" variant="outline" onClick={loadData} className="border-white/10 text-gray-400" data-testid="refresh-vendor-mgmt">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="vendor-mgmt-stats">
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
          <p className="text-lg font-bold heading-font text-[#F7931A]">{registrations.length}</p>
          <p className="text-[9px] text-gray-500">Total Registrations</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
          <p className="text-lg font-bold heading-font text-amber-400">{pendingCount}</p>
          <p className="text-[9px] text-gray-500">Pending Approval</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
          <p className="text-lg font-bold heading-font text-purple-400">{activeStaged}</p>
          <p className="text-[9px] text-gray-500">Active Staged Mints</p>
        </div>
        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
          <p className="text-lg font-bold heading-font text-red-400">{delistedCount}</p>
          <p className="text-[9px] text-gray-500">Delisted</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/[0.03] border border-white/5" data-testid="vendor-mgmt-tabs">
          <TabsTrigger value="queue" className="text-xs gap-1.5" data-testid="tab-queue">
            <Shield className="w-3 h-3" />Queue {pendingCount > 0 && <span className="text-[9px] text-[#F7931A]">({pendingCount})</span>}
          </TabsTrigger>
          <TabsTrigger value="staged" className="text-xs gap-1.5" data-testid="tab-staged">
            <Clock className="w-3 h-3" />Staged Mints
          </TabsTrigger>
          <TabsTrigger value="quality" className="text-xs gap-1.5" data-testid="tab-quality">
            <TrendingUp className="w-3 h-3" />Quality
          </TabsTrigger>
        </TabsList>

        {/* Registration Queue */}
        <TabsContent value="queue" className="space-y-3 mt-4" data-testid="queue-content">
          {registrations.length === 0 ? (
            <p className="text-gray-600 text-center py-12 text-sm">No vendor registrations yet.</p>
          ) : (
            registrations.map(reg => (
              <RegistrationCard key={reg.registration_id} reg={reg} onApprove={handleApprove} onReject={handleReject} />
            ))
          )}
        </TabsContent>

        {/* Staged Mints */}
        <TabsContent value="staged" className="space-y-3 mt-4" data-testid="staged-content">
          {stagedMints.length === 0 ? (
            <p className="text-gray-600 text-center py-12 text-sm">No staged mints active. Approve a vendor registration to create one.</p>
          ) : (
            stagedMints.map(s => (
              <StagedMintCard key={s.vendor_id} staged={s} onRelease={handleReleasePhase} onClawback={handleClawback} />
            ))
          )}
        </TabsContent>

        {/* Quality & Delisting */}
        <TabsContent value="quality" className="space-y-3 mt-4" data-testid="quality-content">
          <div className="flex items-center justify-end mb-2">
            <Button size="sm" variant="outline" className="text-[10px] h-7 border-white/10 text-gray-400" onClick={handleBulkCheck} data-testid="bulk-check-btn">
              <Zap className="w-3 h-3 mr-1.5" />Bulk Quality Check
            </Button>
          </div>
          {vendors.length === 0 ? (
            <p className="text-gray-600 text-center py-12 text-sm">No vendors registered yet.</p>
          ) : (
            <div className="space-y-2">
              {vendors.map(v => (
                <VendorQualityRow
                  key={v.vendor_id}
                  vendor={{
                    ...v,
                    quality_score: v.quality_score || v.token_buckets?.staking || 0,
                  }}
                  onCheckDelist={handleCheckDelist}
                  onReinstate={handleReinstate}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
